import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Paper,
  Grid,
  Typography,
  Chip,
  Button,
  IconButton,
  Divider,
} from '@mui/material';
import { Pencil, Trash2, Plus } from 'lucide-react';
import AsyncCustomSelect from '../../../../../ui/custom_react_select/async_select';
import { CustomSelect } from '../../../../../ui/custom_react_select/custom_select';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { Assessment, PreScreenQuestion } from '../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { usePreScreenQuestions } from './Hooks/UsePreScreenQuestions';

interface IPreScreenQuestions {
  assessmentInfo: Assessment;
  setAssessmentInfo: Dispatch<SetStateAction<Assessment | undefined>>;
}

const questionType: SelectOptions[] = [
  {
    label: 'Numeric',
    value: 'numeric',
  },
  {
    label: 'Multiple Choice',
    value: 'multiple-choice',
  },
  {
    label: 'Video',
    value: 'video',
  },
];

export const PreScreenQuestions: React.FC<IPreScreenQuestions> = (props) => {
  const { assessmentInfo, setAssessmentInfo } = props;
  const { t } = useTranslation(NS.adminPages);
  const AA = ADMIN_PAGES_KEYS.jobOpenings.autoAssessTab;
  const {
    choices,
    defaultOptions,
    editChoices,
    setNewQuestionType,
    handleSaveQuestion,
    handleEditQuestion,
    handleDeleteQuestion,
    handleQuestionChange,
    handleChoiceChange,
    handleAddQuestion,
    loadOptionsDebounced,
    setNewQuestionText,
    setEditChoices,
    editQuestionType,
    setEditQuestionType,
    editQuestionText,
    setEditQuestionText,
    editIndex,
    questions,
    newQuestionText,
    newQuestionType,
    setChoices,
    setEditIndex,
  } = usePreScreenQuestions({ assessmentInfo, setAssessmentInfo });

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {/* ─── Entry Section ─── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          bgcolor: 'action.hover',
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 4,
              height: 20,
              bgcolor: 'success.main',
              borderRadius: 1,
            }}
          />
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {t(AA.addQuestions)}
          </Typography>
        </Stack>

        <Grid container spacing={2} alignItems="flex-end">
          {/* Bank Search */}
          <Grid item xs={12} lg={5}>
            <AsyncCustomSelect
              name="preScreenQuestion"
              value={[]}
              placeholder={t(AA.searchFromBank)}
              defaultOptions={defaultOptions}
              onChange={handleQuestionChange}
              loadOptions={(inputValue, callback) =>
                loadOptionsDebounced(inputValue, callback)
              }
              label={t(AA.importFromBank)}
            />
          </Grid>

          {/* Divider For Desktop */}
          <Grid
            item
            xs={12}
            lg={1}
            sx={{
              display: { xs: 'none', lg: 'flex' },
              justifyContent: 'center',
              pb: 1,
            }}
          >
            <Divider orientation="vertical" flexItem sx={{ height: 40 }} />
          </Grid>

          {/* Manual Entry */}
          <Grid item xs={12} lg={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextInput
                  label={t(AA.manualQuestion)}
                  name="question"
                  value={newQuestionText}
                  type="text"
                  placeholder={t(AA.typeQuestion)}
                  onChange={(e) => setNewQuestionText(e.target.value)}
                  disabled={editIndex !== null}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  label={t(AA.questionType)}
                  name="questionType"
                  value={questionType.find(
                    (option) => option.value === newQuestionType,
                  )}
                  options={questionType}
                  onChange={(selectedOption: SelectOptions) =>
                    setNewQuestionType(
                      selectedOption.value as
                        | 'numeric'
                        | 'multiple-choice'
                        | 'video',
                    )
                  }
                  disabled={editIndex !== null}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Choices Section for New Question */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'flex-end' }}
          spacing={2}
        >
          {newQuestionType === 'multiple-choice' && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {t(AA.options)}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
                {choices.map((choice, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    <TextInput
                      name="choice"
                      value={choice}
                      type="text"
                      placeholder={`Option ${index + 1}`}
                      sx={{ width: { xs: '100%', sm: 192 } }}
                      onChange={(e) =>
                        handleChoiceChange(index, e.target.value, false)
                      }
                    />
                    {choices.length > 1 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setChoices(choices.filter((_, i) => i !== index))
                        }
                        title="Remove Option"
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    )}
                  </Stack>
                ))}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Plus size={14} />}
                  onClick={() => setChoices([...choices, ''])}
                  sx={{ textTransform: 'none', height: 38 }}
                >
                  {t(AA.addOption)}
                </Button>
              </Stack>
            </Box>
          )}

          <Box sx={{ ml: 'auto', pt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAddQuestion()}
              disabled={
                newQuestionText.trim() === '' ||
                (newQuestionType === 'multiple-choice' &&
                  choices.every((choice) => choice.trim() === '')) ||
                editIndex !== null
              }
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                height: 40,
                borderRadius: 1.5,
                px: 3,
              }}
            >
              {t(AA.addToAssessment)}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* ─── Questions List Section ─── */}
      {questions.length > 0 && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 4,
                  height: 20,
                  bgcolor: 'text.disabled',
                  borderRadius: 1,
                }}
              />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {t(AA.activeQuestions)}
              </Typography>
            </Stack>
            <Chip
              size="small"
              label={`${questions.length} ${t(AA.total)}`}
              sx={{ fontWeight: 700, fontSize: '0.75rem' }}
            />
          </Stack>

          <Stack spacing={2}>
            {questions.map((question, indexNum) => (
              <Paper
                key={question.questionId}
                variant="outlined"
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 2.5,
                  transition: 'all 0.2s ease',
                  borderColor:
                    question.questionId === editIndex
                      ? 'primary.main'
                      : 'divider',
                  bgcolor:
                    question.questionId === editIndex
                      ? 'primary.lighter'
                      : 'background.paper',
                  boxShadow:
                    question.questionId === editIndex
                      ? '0 0 0 2px rgba(25, 118, 210, 0.15)'
                      : 'none',
                }}
              >
                {question.questionId === editIndex ? (
                  <Stack spacing={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={8}>
                        <TextInput
                          label={`Question ${indexNum + 1}`}
                          name="question"
                          value={editQuestionText}
                          type="text"
                          onChange={(e) => setEditQuestionText(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <CustomSelect
                          label={t(AA.type)}
                          name="questionType"
                          value={questionType.find(
                            (option) => option.value === editQuestionType,
                          )}
                          options={questionType}
                          onChange={(selectedOption: SelectOptions) =>
                            setEditQuestionType(
                              selectedOption.value as
                                | 'numeric'
                                | 'multiple-choice'
                                | 'video',
                            )
                          }
                        />
                      </Grid>
                    </Grid>

                    {editQuestionType === 'multiple-choice' && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          bgcolor: 'action.hover',
                          borderRadius: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase' }}
                        >
                          {t(AA.options)}
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1.5}>
                          {editChoices.map((choice, index) => (
                            <Paper
                              key={index}
                              variant="outlined"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: 0.5,
                                px: 1,
                                borderRadius: 1.5,
                              }}
                            >
                              <TextInput
                                name="choice"
                                value={choice}
                                type="text"
                                sx={{
                                  width: 192,
                                  '& .MuiOutlinedInput-root': {
                                    height: 32,
                                    '& fieldset': { border: 'none' },
                                  },
                                }}
                                onChange={(e) =>
                                  handleChoiceChange(
                                    index,
                                    e.target.value,
                                    true,
                                  )
                                }
                              />
                              {editChoices.length > 1 && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    setEditChoices(
                                      editChoices.filter((_, i) => i !== index),
                                    )
                                  }
                                >
                                  <Trash2 size={14} />
                                </IconButton>
                              )}
                            </Paper>
                          ))}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Plus size={14} />}
                            onClick={() => setEditChoices([...editChoices, ''])}
                            sx={{ textTransform: 'none', height: 36 }}
                          >
                            {t(AA.addOption)}
                          </Button>
                        </Stack>
                      </Paper>
                    )}

                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={1.5}
                      sx={{ pt: 1 }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => setEditIndex(null)}
                        sx={{ textTransform: 'none' }}
                      >
                        {t(AA.cancel)}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleSaveQuestion()}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {t(AA.saveChanges)}
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          bgcolor: 'action.hover',
                          color: 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          border: '1px solid',
                          borderColor: 'divider',
                          flexShrink: 0,
                        }}
                      >
                        {indexNum + 1}
                      </Box>

                      <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                          <Typography variant="body1" fontWeight={600} color="text.primary">
                            {question.question}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              questionType.find(
                                (item) => item.value === question.questionType,
                              )?.label || question.questionType
                            }
                            color={
                              question.questionType === 'numeric'
                                ? 'info'
                                : question.questionType === 'video'
                                ? 'secondary'
                                : 'success'
                            }
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        </Stack>

                        {question.questionType === 'multiple-choice' &&
                          question.choices && (
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                              {question.choices.map((choice, idx) => (
                                <Chip
                                  key={idx}
                                  size="small"
                                  label={choice}
                                  sx={{
                                    bgcolor: 'action.hover',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                  }}
                                />
                              ))}
                            </Stack>
                          )}
                      </Stack>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0, ml: { xs: 5, sm: 0 } }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditQuestion(question)}
                        title="Edit"
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                      >
                        <Pencil size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          handleDeleteQuestion(question.questionId)
                        }
                        title="Delete"
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Stack>
                  </Stack>
                )}
              </Paper>
            ))}
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};
